import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { notify } from "../util/notify";
import { useUser } from "../store";
import "./FacilityDashboard.scss";
import { toPrettyCurrency, toMonthYear } from "common";
import { useReports } from "../store/reports";
import { StatusIcon } from "./StatusIcon";
import { SlugLink } from "./SlugLink";
import { ReportRow } from "./ReportRow";
import { AgingReportBalanceStatusType } from "../util/getAgingReportStatus";
import ReportMessages from "./ReportMessages";
import clsx from 'clsx';
import Modal from "./Modal";
import UploadStatus from "./UploadStatus";
import ReportUpload from "./ReportUpload";
import { useReportUpload } from "../hooks/useReportUpload";
import FacilityTabs from "./FacilityTabs";
import ChatDock, { MAX_DOCKED_CHATS } from "./ChatDock";
import { rememberDashboard } from "../util/lastViewed";

const STATUS_FILTERS = [
  { label: "Paid", status: AgingReportBalanceStatusType.Paid },
  { label: "Changed", status: AgingReportBalanceStatusType.Changed },
  { label: "Outstanding", status: AgingReportBalanceStatusType.Outstanding },
  { label: "Deadline Passed", status: AgingReportBalanceStatusType.DeadlinePassed },
  { label: "Extended Deadline Passed", status: AgingReportBalanceStatusType.ExtendedDeadlinePassed },
  { label: "Message Provided", status: AgingReportBalanceStatusType.MessageProvided },
  { label: "Archived", status: AgingReportBalanceStatusType.Archived },
];

// Declared at module level so the checkbox keeps its identity (and focus) across dashboard renders
const StatusCheckbox = ({ label, status, statusFilters, setStatusFilters }) => {
  const isSelected = statusFilters.includes(status);

  return (
    <label className={clsx("dashboard-status-filter", `is-${status}`, "checkbox", { "is-selected": isSelected })}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={(e) => {
          setStatusFilters(
            e.target.checked
              ? [...statusFilters, status]
              : statusFilters.filter((s) => s !== status)
          );
        }}
      />{" "}
      {label} <StatusIcon statusType={status} />
    </label>
  );
};

export default function FacilityDashboard() {
  const { invokeApi, user, group, facility, payer } = useUser();
  const tableRef = useRef(null);
  const uploadStatusRef = useRef(null);
  const [activeReport, setActiveReport] = useState();
  // Chat windows docked along the bottom: kept while browsing this payer's table
  const [chats, setChats] = useState([]);
  const openChat = useCallback(
    (report) =>
      setChats((previous) => {
        if (previous.some((chat) => chat.sk === report.sk)) {
          return previous.map((chat) => (chat.sk === report.sk ? { ...chat, minimized: false } : chat));
        }
        return [...previous, { sk: report.sk, minimized: false }].slice(-MAX_DOCKED_CHATS);
      }),
    []
  );
  const closeChat = useCallback((sk) => setChats((previous) => previous.filter((chat) => chat.sk !== sk)), []);
  const toggleChat = useCallback(
    (sk) => setChats((previous) => previous.map((chat) => (chat.sk === sk ? { ...chat, minimized: !chat.minimized } : chat))),
    []
  );
  // The windows belong to the payer being shown
  useEffect(() => {
    setChats([]);
  }, [facility, payer]);
  // So the tabs and the facility list can bring you back to where you were
  useEffect(() => {
    rememberDashboard(user.username, facility, payer);
  }, [user.username, facility, payer]);
  const {
    reports,
    months,
    displayableReports,
    totals,
    setSearchTerm,
    statusFilters,
    setStatusFilters,
    refresh,
    loaded,
    failed,
  } = useReports({ live: chats.length > 0 || Boolean(activeReport) });
  const [searchParams, setSearchParams] = useSearchParams();
  const alertContext = useMemo(
    () => ({ username: user.username, isAdmin: user.type === "Admin", facility, payer }),
    [user.username, user.type, facility, payer]
  );
  const requestedReport = searchParams.get("report");
  
  const [searchQuery, setSearchQuery] = useState("");
  const totalReportsOnLoad = reports.filter((report) => !report.archived).length;

  // Links in emails and search results point at one resident: open that resident's messages
  useEffect(() => {
    if (!requestedReport) return;
    const match = reports.find((report) => report.sk === requestedReport);
    if (!match && !loaded) return;
    if (match) openChat(match);
    else notify.error("That claim is no longer on this report.");
    const remaining = new URLSearchParams(searchParams);
    remaining.delete("report");
    setSearchParams(remaining, { replace: true });
  }, [requestedReport, reports, loaded, searchParams, setSearchParams, openChat]);

  const upload = useReportUpload({
    invokeApi,
    scopeKey: `${facility}#${payer}`,
    enabled: user.type === "Admin",
    onProcessed: refresh,
  });

  const statusFiltersBar = (
    <div className="status-key dashboard-status-key" aria-label="Filter reports by status">
      <span className="key-specifier">Status</span>
      {STATUS_FILTERS.map(({ label, status }) => (
        <StatusCheckbox
          key={status}
          label={label}
          status={status}
          statusFilters={statusFilters}
          setStatusFilters={setStatusFilters}
        />
      ))}
    </div>
  );

  const onTableScroll = (e) => {
    localStorage.setItem(`${facility}#${payer}#scrollTop`, e.target.scrollTop);
  };

  useEffect(() => {
    if (tableRef.current)
      tableRef.current.scrollTop =
        localStorage.getItem(`${facility}#${payer}#scrollTop`) || 0;
  }, [displayableReports, facility, payer]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveReport(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // The table is fixed to the window, so it has to start below everything above it. Measured, because the height of
  // that part changes with the upload status, the tabs and how the toolbar wraps.
  useEffect(() => {
    const holder = uploadStatusRef.current;
    const root = holder?.closest(".facility-dashboard");
    if (!holder || !root) return undefined;
    const apply = () => {
      root.style.setProperty("--upload-status-height", `${holder.offsetHeight}px`);
      const bottom = holder.getBoundingClientRect().bottom + window.scrollY;
      // 6px under the payers, the same gap the empty state gets; the table's 1px border is part of it
      root.style.setProperty("--dashboard-table-top", `${Math.ceil(bottom) + 5}px`);
    };
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(holder);
    observer.observe(root);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--upload-status-height");
      root.style.removeProperty("--dashboard-table-top");
    };
  }, []);
  // Sits beside the payer pills, above the table
  const searchBox = (
    <>
      {totalReportsOnLoad > 0 && (
        <div
          className="dashboard-report-count"
          aria-label={`${displayableReports.length} of ${totalReportsOnLoad} reports`}
        >
          <strong>{displayableReports.length}</strong>
          <span>of {totalReportsOnLoad}</span>
          <span>reports</span>
        </div>
      )}
      {user.type === "Admin" && (
        <div className="actions dashboard-toolbar">
          <div className="field is-pulled-right is-grouped">
            <div className="control">
              <ReportUpload busy={upload.busy} onUpload={upload.start} />
            </div>
            <div className="control">
              <SlugLink
                path={[group, facility, payer, "backups"]}
                className="button is-info"
              >
                Backups
              </SlugLink>
            </div>
          </div>
        </div>
      )}
      <div className="field dashboard-search">
        <div className="control has-icons-left">
          <input
            className="input"
            type="text"
            placeholder="Search residents or bill types"
            aria-label="Search residents or bill types"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSearchTerm(e.target.value);
            }}
          />
          {searchQuery && (
            <button
              className="dashboard-search-clear"
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setSearchQuery("");
                setSearchTerm("");
              }}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          )}
          <span className="icon is-small is-left">
            <i className="fa-solid fa-magnifying-glass"></i>
          </span>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Modal isActive={!!activeReport} onCancel={() => setActiveReport(null)} >      
        {activeReport && (
          <ReportMessages
            report={reports.find((report) => report.sk === activeReport.sk) ?? activeReport}
            onCancel={() => setActiveReport(null)}
            onDock={() => {
              openChat(activeReport);
              setActiveReport(null);
            }}
          />
        )}
      </Modal>
      <ChatDock
        chats={chats}
        reports={reports}
        username={user.username}
        onToggle={toggleChat}
        onClose={closeChat}
        onExpand={(report) => {
          closeChat(report.sk);
          setActiveReport(report);
        }}
      />
      <div className="content facility-dashboard">
        <h1 className="dashboard-title">
          {facility} / {payer}
        </h1>

        {/* Everything above the table is measured, so the table sits below it */}
        <div ref={uploadStatusRef} className="dashboard-upload-status">
          <UploadStatus state={upload.state} onDismiss={upload.dismiss} />
          <FacilityTabs search={searchBox} />
        </div>
        <div className="dashboard-results">
          {!reports.length ? (
            <>
              {statusFiltersBar}
              {failed ? (
                <div className="dashboard-empty-state" role="alert">
                  <span className="dashboard-empty-icon" aria-hidden="true">
                    <i className="fa-solid fa-triangle-exclamation" />
                  </span>
                  <h2>Could not load the reports</h2>
                  <p>Check your connection and try again.</p>
                  <button className="button is-light" type="button" onClick={refresh}>
                    Try again
                  </button>
                </div>
              ) : loaded ? (
                <div className="dashboard-empty-state">
                  <span className="dashboard-empty-icon" aria-hidden="true">
                    <i className="fa-solid fa-file-invoice-dollar" />
                  </span>
                  <h2>No aging reports yet</h2>
                  <p>Upload an aging report to start tracking claims for this facility.</p>
                </div>
              ) : (
                <div className="dashboard-empty-state" role="status">
                  <span className="dashboard-empty-icon" aria-hidden="true">
                    <i className="fa-solid fa-spinner fa-spin-pulse" />
                  </span>
                  <h2>Loading reports</h2>
                </div>
              )}
            </>
          ) : !displayableReports.length ? (
            <>
              {statusFiltersBar}
              <div className="dashboard-empty-state dashboard-no-results">
                <span className="dashboard-empty-icon" aria-hidden="true">
                  <i className="fa-solid fa-magnifying-glass" />
                </span>
                <h2>No matching reports</h2>
                <p>Try a different search or clear the active status filters.</p>
                <button
                  className="button is-light"
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setSearchTerm("");
                    setStatusFilters([]);
                  }}
                >
                  Clear search and filters
                </button>
              </div>
            </>
          ) : (
            <div
              className="table-container no-wrap dashboard-table-container"
              ref={tableRef}
              onScroll={onTableScroll}
            >
              {statusFiltersBar}
              <table className="is-size-7 is-clearfix table is-bordered is-striped is-narrow is-hoverable is-fullwidth has-text-centered">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Bill Type</th>
                    {months?.map((month) => (
                      <th key={month}>{toMonthYear(month)}</th>
                    ))}

                    <th>Prior</th>
                    <th>Net</th>
                    <th>Medicaid Application</th>
                    <th>Aging Status</th>
                  </tr>
                </thead>
                <tbody>
                  {displayableReports?.map((report) => (
                    <ReportRow
                      report={report}
                      statusFilters={statusFilters}
                      key={report.pk + report.sk}
                      months={months}
                      setActiveReport={openChat}
                      openChat={openChat}
                      alertContext={alertContext}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Total: {displayableReports.length}</th>
                    <th></th>

                    {months?.map((month) => (
                      <th key={month}>{toPrettyCurrency(totals[month])}</th>
                    ))}
                    <th>{toPrettyCurrency(totals.prior)}</th>
                    <th>{toPrettyCurrency(totals.net)}</th>
                    <th></th>
                    <th></th>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
